import { AgenciesService } from './agenciesService';
import { apiClient } from '../utils/api';

interface MenuPermission {
  key: string;
  label: string;
  enabled: boolean;
}

interface AgencyMenuConfig {
  agencyId: string;
  agencyName?: string;
  menuPermissions: MenuPermission[];
}

// API Response interfaces
interface AgencyMenuConfigResponse {
  status: string;
  message: string;
  data: AgencyMenuConfig;
}

interface AgencyMenuConfigListResponse {
  status: string;
  message: string;
  data: AgencyMenuConfig[];
}

// Default menu configuration
const defaultMenuPermissions: MenuPermission[] = [
  { key: 'dashboard', label: 'Dashboard', enabled: true },
  { key: 'patients', label: 'Clients', enabled: true },
  { key: 'caregivers', label: 'Caregivers', enabled: true },
  { key: 'matching', label: 'Matching', enabled: true },
  { key: 'shifts', label: 'Shift Management', enabled: true },
  { key: 'invoices', label: 'Invoices', enabled: true },
  { key: 'templates', label: 'Templates', enabled: true },
  { key: 'checklist', label: 'Checklist', enabled: true },
  { key: 'tasks', label: 'Tasks', enabled: true },
  { key: 'staff', label: 'Staff', enabled: true },
  { key: 'knowledge', label: 'Wissen', enabled: true },
  { key: 'files', label: 'Daten', enabled: true },
  { key: 'settings', label: 'Settings', enabled: true },
];

export const agencyMenuConfigApi = {
  async getAgencyMenuConfig(agencyId: string): Promise<AgencyMenuConfig> {
    try {
      const response = await apiClient.get<AgencyMenuConfigResponse>(`/agency-menu-config/${agencyId}`);
      return response.data.data;
    } catch (error: any) {
      // If config doesn't exist, return default configuration
      if (error.response?.status === 404) {
        console.log(`No menu config found for agency ${agencyId}, using defaults`);
        return {
          agencyId,
          menuPermissions: defaultMenuPermissions,
        };
      }
      throw error;
    }
  },

  async getAllAgencyMenuConfigs(): Promise<AgencyMenuConfig[]> {
    try {
      // Get real agencies from AgenciesService
      const agencies = await AgenciesService.getAgencies();
      
      // Get all menu configurations from backend
      const response = await apiClient.get<AgencyMenuConfigListResponse>('/agency-menu-config');
      const existingConfigs = response.data.data;
      
      // Create a map for quick lookup
      const configMap = new Map(existingConfigs.map(config => [config.agencyId, config]));
      
      // Return configurations for all agencies, using defaults where not configured
      return agencies.map(agency => {
        const existingConfig = configMap.get(agency.id);
        return {
          agencyId: agency.id,
          agencyName: agency.name,
          menuPermissions: existingConfig?.menuPermissions || defaultMenuPermissions,
        };
      });
    } catch (error) {
      console.error('Failed to fetch agency menu configurations:', error);
      // Fallback to just agencies with default configs
      try {
        const agencies = await AgenciesService.getAgencies();
        return agencies.map(agency => ({
          agencyId: agency.id,
          agencyName: agency.name,
          menuPermissions: defaultMenuPermissions,
        }));
      } catch (agencyError) {
        console.error('Failed to fetch agencies:', agencyError);
        return [];
      }
    }
  },

  async updateAgencyMenuConfig(agencyId: string, menuPermissions: MenuPermission[]): Promise<void> {
    try {
      await apiClient.put(`/agency-menu-config/${agencyId}`, {
        agencyId,
        menuPermissions,
      });
      console.log(`Successfully updated menu config for agency ${agencyId}`);
    } catch (error) {
      console.error(`Failed to update menu config for agency ${agencyId}:`, error);
      throw error;
    }
  },

  async updateMultipleAgencyMenuConfigs(configs: AgencyMenuConfig[]): Promise<void> {
    try {
      await apiClient.put('/agency-menu-config/bulk', {
        configs: configs.map(config => ({
          agencyId: config.agencyId,
          menuPermissions: config.menuPermissions,
        })),
      });
      console.log('Successfully updated multiple agency menu configurations');
    } catch (error) {
      console.error('Failed to update multiple agency menu configurations:', error);
      throw error;
    }
  },

  async deleteAgencyMenuConfig(agencyId: string): Promise<void> {
    try {
      await apiClient.delete(`/agency-menu-config/${agencyId}`);
      console.log(`Successfully deleted menu config for agency ${agencyId}`);
    } catch (error) {
      console.error(`Failed to delete menu config for agency ${agencyId}:`, error);
      throw error;
    }
  },
};

export type { MenuPermission, AgencyMenuConfig }; 